<template>
  <div class="w-100 grid-container">
    <vt-side-panel :visible="showSidePanel">
      <configuration-sidepanel
        :icon="'fal fa-users'"
        :show-operation-group-filter="true"
        :show-center-filter="true"
        :association-types="associationTypes"
        :associations-list="associationsList"
        :has-tabs="true"
        :sidepanel-title="sidepanelTitle"
        @closeConfigurationSidepanel="showSidePanel = false"
        @update="onSidePanelUpdate"
      />
    </vt-side-panel>
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-users'"
      :section-title="$tc('configuration.sections.users', 2)"
      :section-subtitle="msgSelectedRows"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteUserapps(selectedRows)"
        @shortkey="deleteUserapps(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="
          showUserappModal(
            $t('configuration.actions.update'),
            selectedRows[0]
          )
        "
        @shortkey="
          showUserappModal(
            $t('configuration.actions.update'),
            selectedRows[0]
          )
        "
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showUserappModal($t('configuration.actions.add'))"
        @shortkey="showUserappModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        class="configuration-grid people-userapp-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="userappModalWrapper">
      <userapp-modal
        ref="userappModal"
        :action-title="actionTitle"
        @update="patchUserapps($event)"
        @create="createUserapp($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import columnsUserappGrid from '@/modules/configurationModule/detail/peopleManagement/userapp/columnsUserappGrid'
import UserappModal from '@/modules/configurationModule/detail/peopleManagement/userapp/UserappModal'
import ConfigurationSidepanel from '@/modules/configurationModule/detail/ConfigurationSidepanel'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import UserappConfigurationServices from '@/api/resources/configuration/peopleManagement/userapp/UserappConfigurationServices'
import UserappOperationConfigurationServices from '@/api/resources/configuration/peopleManagement/userapp/UserappOperationConfigurationServices'
import UserappRoleConfigurationServices from '@/api/resources/configuration/peopleManagement/userapp/UserappRoleConfigurationServices'
import PetitionerConfigurationServices from '@/api/resources/configuration/peopleManagement/petitioner/PetitionerConfigurationServices'
import PersonnelConfigurationServices from '@/api/resources/configuration/peopleManagement/personnel/PersonnelConfigurationServices'
import { ASSOCIATION_TYPE } from '@/api/resources/configuration/peopleManagement/AssociationTypes'
import { uniqBy } from 'lodash'
import { UserType } from '@/api/resources/common/initialization/common-initialization.dto.ts'

export default {
    name: 'PeopleUserappGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        UserappModal,
        ConfigurationSidepanel
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.peopleManagement.userapp',
            showSidePanel: false,
            sidepanelTitle: null,
            actionTitle: null,
            clickedRow: null,
            selectedRows: [],
            selectedUserappOperations: [],
            selectedUserappRoles: [],
            allSelectedUserappOperations: [],
            allSelectedUserappRoles: [],
            associationTypes: [
                ASSOCIATION_TYPE.ROLE,
                ASSOCIATION_TYPE.OPERATION
            ],
            columnDefs: columnsUserappGrid(),
            associationsList: {},
            mode: {
                size: 25
            }
        }
    },
    computed: {
        msgSelectedRows() {
            return this.selectedRows.length > 0
                ? `${this.selectedRows.length} ${this.$tc(
                    'global.selectedElements',
                    this.selectedRows.length
                )}`
                : ''
        },
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                onRowClicked: this.onRowClicked,
                getContextMenuItems: this.getContextMenuItems,
                getRowHeight: () => this.rowHeight,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged
            }
        },
        hasActiveUserapp() {
            return !!this.selectedRows.find(userapp => userapp.active)
        },
        hasDeactiveUserapp() {
            return !!this.selectedRows.find(userapp => !userapp.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === this.selectedRows.filter(userapp => userapp.active).length
        },
        hasAllDeactivated() {
            return this.selectedRows.length === this.selectedRows.filter(userapp => !userapp.active).length
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActiveUserapp && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactiveUserapp && !this.hasAllActivated
        }
    },
    async mounted() {
        if (this.$route.params.actions) {
            let userapp = {}
            let actionTitle = null
            if (this.$route.params.actions.updateUser) {
                const [err, userappResponse] = await this.toCall(
                    UserappConfigurationServices.getUserappById(
                        this.$route.params.actions.updateUser.userId
                    )
                )
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
                actionTitle = this.$tc('configuration.actions.update')
                userapp = userappResponse
            } else if (this.$route.params.actions.createUser) {
                if (this.$route.params.actions.createUser.petitionerId) {
                    const [err, petitionerResponse] = await this.toCall(PetitionerConfigurationServices.getPetitionerById(this.$route.params.actions.createUser.petitionerId))
                    if (err) {
                        this.showErrorModalGlobal()
                        return
                    }
                    userapp.petitioner = petitionerResponse
                    userapp.userType = UserType.PETITIONER
                    userapp.active = true
                } else if (this.$route.params.actions.createUser.personnelId) {
                    const [err, personnelResponse] = await this.toCall(
                        PersonnelConfigurationServices.getPersonnelById(
                            this.$route.params.actions.createUser.personnelId
                        )
                    )
                    if (err) {
                        this.showErrorModalGlobal()
                        return
                    }
                    userapp.personnel = personnelResponse
                    userapp.userType = UserType.PERSONNEL
                    userapp.active = true
                }
                actionTitle = this.$t('configuration.actions.add')
            }
            await this.showUserappModal(actionTitle, userapp)
        }
    },
    methods: {
        onSidePanelUpdate(sidePanelEvent) {
            this.showUserappModal(
                $tc('configuration.actions.update'),
                this.clickedRow,
                sidePanelEvent
            )
            this.showSidePanel = false
        },
        async activateDeactivateSelection(option, selectionBatch) {
            const userappsToPatch = selectionBatch
                .filter(userapp => userapp.active !== option)
                .map(userapp => ({
                    active: option,
                    id: userapp.id
                })
                )
            if (userappsToPatch.length) {
                this.updateLoading(true)
                const [err] = await this.toCall(
                    UserappConfigurationServices.patchUserappsBatch(
                        userappsToPatch
                    )
                )
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
                if (selectionBatch.length > 1) {
                    this.showToastSuccessGlobal(
                        this.$tc('configuration.toastMessages.updated', 2, {
                            elements: selectionBatch.length
                        })
                    )
                } else {
                    this.showToastSuccessGlobal(
                        this.$tc('configuration.toastMessages.updated', 1)
                    )
                }
                this.reloadData()
            }
        },
        async patchUserapps(...userapps) {
            let userappsToPatch = []
            for (const userapp of userapps) {
                if (userapp.removeUserLastProfileImage) {
                    this.updateLoading(true)
                    const [deleteLastProfileErr] = await this.toCall(
                        UserappConfigurationServices.deleteLastProfileImage(userapp.id)
                    )
                    this.updateLoading(false)
                    if (deleteLastProfileErr) {
                        this.showErrorModalGlobal()
                        return
                    }
                } else if (userapp.selectedProfileImageBlob) {
                    this.updateLoading(true)
                    const [replaceProfileImageForUserErr] = await this.toCall(
                        UserappConfigurationServices.replaceProfileImageForUser(
                            userapp.selectedProfileImageBlob,
                            userapp.id
                        )
                    )
                    this.updateLoading(false)
                    if (replaceProfileImageForUserErr) {
                        this.showErrorModalGlobal()
                        return
                    }
                }
                const userappPatchInBatchRequests = {
                    active: userapp.active,
                    id: userapp.id,
                    userType: userapp.userType,
                    languageId: userapp.language.id,
                    personnelId: userapp.personnel?.id ?? null,
                    petitionerId: userapp.petitioner?.id ?? null,
                    patientId: userapp.patient?.id ?? null,
                    reportingLanguageId: userapp.reportingLanguageId,
                    username: userapp.username
                }
                userappsToPatch.push(userappPatchInBatchRequests)
            }
            this.updateLoading(true)
            const [err, data] = await this.toCall(
                UserappConfigurationServices.patchUserappsBatch(userappsToPatch)
            )
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            } else if (data.length) {
                this.handleErrors(data)
                return
            }
            this.$refs.userappModal.hide()
            this.showToastSuccessGlobal(
                this.$tc('configuration.toastMessages.updated', 1)
            )
            this.reloadData()
        },
        async createUserapp(userapp) {
            const userappCreateRequest = {
                active: userapp.active,
                userType: userapp.userType,
                languageId: userapp.language?.id ?? null,
                personnelId: userapp.personnel?.id ?? null,
                petitionerId: userapp.petitioner?.id ?? null,
                patientId: userapp.patient?.id ?? null,
                reportingLanguageId: userapp.reportingLanguage?.id ?? null,
                username: userapp.username,
                password: userapp.password
            }
            this.updateLoading(true)
            const [createUserappErr, createdUserapp] = await this.toCall(
                UserappConfigurationServices.createUserapp(userappCreateRequest)
            )
            this.updateLoading(false)
            if (createUserappErr) {
                let errMessage = ''
                if (
                    createUserappErr.response.data &&
                    createUserappErr.response.data.message
                ) {
                    errMessage = createUserappErr.response.data.message
                } else errMessage = createUserappErr
                this.showErrorModalGlobal(errMessage)
                return
            }
            if (userapp.userPictureBlob) {
                this.updateLoading(true)
                const [replaceProfileImageForUserErr] = await this.toCall(
                    UserappConfigurationServices.replaceProfileImageForUser(
                        userapp.userPictureBlob,
                        createdUserapp.id
                    )
                )
                this.updateLoading(false)
                if (replaceProfileImageForUserErr) {
                    this.showErrorModalGlobal(replaceProfileImageForUserErr)
                    return
                }
            }
            this.$refs.userappModal.initUserapp(createdUserapp)
            await this.$refs.userappModal.initRolesAndOperations()
            this.actionTitle = this.$tc('configuration.actions.update')
            this.showToastSuccessGlobal(
                this.$tc('configuration.toastMessages.created')
            )
            this.reloadData()
        },
        async deleteUserappsBatch(userapps) {
            const userappIdsToDelete = userapps.map(userapp => userapp.id)
            this.updateLoading(true)
            const [err, response] = await this.toCall(
                UserappConfigurationServices.deleteUserappsBatch(
                    userappIdsToDelete
                )
            )
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            } else if (response.length) {
                this.handleErrors(response)
                return
            }
            if (userappIdsToDelete.length > 1) {
                this.showToastSuccessGlobal(
                    this.$tc('configuration.toastMessages.deleted', 2, {
                        elements: userappIdsToDelete.length
                    })
                )
            } else {
                this.showToastSuccessGlobal(
                    this.$tc('configuration.toastMessages.deleted', 1)
                )
            }
            this.reloadData()
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(
                new PaginatorDatasource(UserappConfigurationServices)
            )
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () =>
                            this.showUserappModal(
                                this.$t('configuration.actions.update'),
                                params?.node.data
                            )
                    },
                    {
                        name: this.selectedRows.length
                            ? this.$t('configuration.actions.deleteSelection')
                            : this.$t('configuration.actions.delete'),
                        action: () => {
                            const userappsToDelete = this.selectedRows.length
                                ? this.selectedRows
                                : [params?.node.data]
                            this.deleteUserapps(userappsToDelete)
                        }
                    },
                    {
                        name: params?.node.data.active
                            ? this.$t('configuration.actions.deactivate')
                            : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () =>
                            this.activateDeactivateSelection(
                                !params.node?.data.active,
                                [params?.node.data]
                            )
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () =>
                            this.activateDeactivateSelection(
                                true,
                                this.selectedRows
                            )
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () =>
                            this.activateDeactivateSelection(
                                false,
                                this.selectedRows
                            )
                    }
                ]
            }
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        },
        setColumns(columns) {
            let columnDefs = columns
            if (!this.globalParameters.middleNameSupported) {
                columnDefs = columns.filter(
                    col => (col.colId ?? col.field) !== 'middleName'
                )
            }
            return columnDefs
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        async getAssociations(id) {
            this.updateLoading(true)
            const [err, response] = await this.toCall(
                Promise.all([
                    UserappOperationConfigurationServices.getUserappOperationsByUserappId(id),
                    UserappRoleConfigurationServices.getUserappRolesByUserappId(id)
                ])
            )
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            [this.allSelectedUserappOperations, this.allSelectedUserappRoles] = response
            this.selectedUserappOperations = uniqBy(this.allSelectedUserappOperations, 'operation.code')
            this.selectedUserappRoles = uniqBy(this.allSelectedUserappRoles, 'role.id')
            this.associationsList = {
                [ASSOCIATION_TYPE.OPERATION.key]: this.allSelectedUserappOperations,
                [ASSOCIATION_TYPE.ROLE.key]: this.allSelectedUserappRoles
            }
        },
        async onRowClicked(params) {
            await this.getAssociations(params?.node.data.id)
            this.clickedRow = params?.node.data
            switch (params?.node.data.userType) {
            case UserType.PETITIONER:
                this.sidepanelTitle = params?.node.data.petitioner.fullName
                break
            case UserType.PERSONNEL:
                this.sidepanelTitle = params?.node.data.personnel.fullName
                break
            case UserType.PATIENT:
                this.sidepanelTitle = params?.node.data.patient.fullName
                break
            }
            this.showSidePanel = true
        },
        onGridReady({ api }) {
            this.gridApi = api
            this.gridApi.setColumnDefs(this.setColumns(this.columnDefs))
            api.setServerSideDatasource(
                new PaginatorDatasource(UserappConfigurationServices)
            )
        },
        async showUserappModal(title, selectedUserapp, associationType) {
            this.actionTitle = title
            this.$refs.userappModalWrapper.show([
                selectedUserapp,
                associationType
            ])
        },
        async deleteUserapps(userapps) {
            if (userapps.length) {
                const promise = await this.showConfirmationModalGlobal(
                    `${this.$tc('configuration.grid.deleteConfirmation')} ${
                        userapps.length
                    } ${this.$tc('configuration.grid.element',
                        userapps.length
                    )}, <br/> ${this.$tc('configuration.grid.deleteQuestion')}`,
                    this.$tc('configuration.actions.delete')
                )
                if (promise) {
                    await this.deleteUserappsBatch(userapps)
                }
            }
        }
    }
}
</script>
